/**
 * Dashboard Page
 * Main dashboard with statistics and quick actions
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useViewMode } from '@/contexts/ViewModeContext';
import { Card } from '@/components/common/Card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ViewModeToggle } from '@/components/common/ViewModeToggle';
import { apiRequest } from '@/api/client';

interface DashboardStats {
  totalClasses: number;
  totalStudents: number;
  totalStories: number;
  licenseCount?: number;
  licensesUsed?: number;
}

export const DashboardPage = () => {
  const { user } = useAuth();
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, [viewMode]);

  const fetchDashboardStats = async () => {
    try {
      setIsLoading(true);
      
      if (user?.isAdmin && viewMode === 'school') {
        // Admin viewing all school data - use get_school_info for comprehensive stats
        const schoolInfoResponse = await apiRequest<any>('get_school_info', {
          adminTeacherId: (user as any).teacherId || user?.userId,
        });
        
        if (schoolInfoResponse.success && (schoolInfoResponse as any).schoolInfo) {
          const schoolInfo = (schoolInfoResponse as any).schoolInfo;
          const stats = schoolInfo.stats || {};
          
          // Fetch students to get accurate story count
          const studentsResponse = await apiRequest<{ students: any[] }>('get_school_students', {
            adminEmail: user?.email,
            schoolId: user?.schoolId,
          });
          
          const students = studentsResponse.success && (studentsResponse as any).students
            ? (studentsResponse as any).students
            : [];
          
          setStats({
            totalClasses: stats.totalClasses || 0,
            totalStudents: stats.totalStudents || 0,
            totalStories: students.reduce((sum: number, s: any) => sum + (s.storiesCreated || 0), 0),
            licenseCount: stats.classLimit || 0,
            licensesUsed: stats.classesUsed || 0,
          });
        } else {
          // Fallback to old method if get_school_info fails
          const classesResponse = await apiRequest<{ classes: any[] }>('get_school_classes', {
            schoolId: user?.schoolId,
          });
          
          const studentsResponse = await apiRequest<{ students: any[] }>('get_school_students', {
            adminEmail: user?.email,
            schoolId: user?.schoolId,
          });
          
          const classes = classesResponse.success && (classesResponse as any).classes 
            ? (classesResponse as any).classes 
            : [];
          
          const students = studentsResponse.success && (studentsResponse as any).students
            ? (studentsResponse as any).students
            : [];
          
          setStats({
            totalClasses: classes.length,
            totalStudents: students.length,
            totalStories: students.reduce((sum: number, s: any) => sum + (s.storiesCreated || 0), 0),
          });
        }
      } else {
        // Personal view - just sum studentCount from classes (no need to fetch individual students)
        const classesResponse = await apiRequest<{ classes: any[] }>('get_teacher_classes', {
          userEmail: user?.email,
        });
        
        const classes = classesResponse.success && (classesResponse as any).classes 
          ? (classesResponse as any).classes 
          : [];
        
        // For personal view, we need to fetch students to get accurate story counts
        const studentMap = new Map();
        
        for (const classItem of classes) {
          try {
            const classStudentsResponse = await apiRequest<{ students: any[] }>('get_class_students', {
              classId: classItem.classId,
            });
            
            if (classStudentsResponse.success && (classStudentsResponse as any).students) {
              for (const student of (classStudentsResponse as any).students) {
                if (!studentMap.has(student.studentId)) {
                  studentMap.set(student.studentId, student);
                }
              }
            }
          } catch (error) {
            console.error(`Failed to fetch students for class ${classItem.classId}:`, error);
          }
        }
        
        const students = Array.from(studentMap.values());
        
        // Calculate stats from actual data
        setStats({
          totalClasses: classes.length,
          totalStudents: students.length,
          totalStories: students.reduce((sum: number, s: any) => sum + (s.storiesCreated || 0), 0),
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setStats({
        totalClasses: 0,
        totalStudents: 0,
        totalStories: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-slate-600">
            {viewMode === 'school' ? "Here's your school overview" : "Here's what's happening with your classes today"}
          </p>
        </div>
        
        <ViewModeToggle />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Total Classes</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{stats?.totalClasses || 0}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Total Students</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{stats?.totalStudents || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Stories Created</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{stats?.totalStories || 0}</p>
            </div>
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* License Usage Card (Admin School View Only) */}
      {user?.isAdmin && viewMode === 'school' && stats?.licenseCount !== undefined && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <p className="text-sm font-medium text-slate-600">School Licenses</p>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-extrabold text-slate-900">
                  {stats.licensesUsed || 0}
                </p>
                <p className="text-lg text-slate-500">
                  / {stats.licenseCount}
                </p>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Usage</span>
                  <span className="font-medium text-slate-900">
                    {stats.licenseCount > 0 
                      ? Math.round((stats.licensesUsed! / stats.licenseCount) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      stats.licensesUsed! >= stats.licenseCount 
                        ? 'bg-red-500' 
                        : stats.licensesUsed! / stats.licenseCount > 0.8
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                    }`}
                    style={{ 
                      width: `${Math.min((stats.licensesUsed! / stats.licenseCount) * 100, 100)}%` 
                    }}
                  />
                </div>
              </div>
              {stats.licensesUsed! >= stats.licenseCount && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  License limit reached
                </p>
              )}
              {stats.licensesUsed! / stats.licenseCount > 0.8 && stats.licensesUsed! < stats.licenseCount && (
                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Approaching license limit
                </p>
              )}
            </div>
            <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <Card title="Quick Actions">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/classes/new')}
            className="p-4 border-2 border-dashed border-slate-200 rounded-2xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left group"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-emerald-200 transition-colors">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Create Class</h3>
            <p className="text-sm text-slate-600">Set up a new class</p>
          </button>

          <button
            onClick={() => navigate('/students/new')}
            className="p-4 border-2 border-dashed border-slate-200 rounded-2xl hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Add Student</h3>
            <p className="text-sm text-slate-600">Register a new student</p>
          </button>

          <button
            onClick={() => navigate('/wordlists/new')}
            className="p-4 border-2 border-dashed border-slate-200 rounded-2xl hover:border-violet-500 hover:bg-violet-50 transition-colors text-left group"
          >
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-violet-200 transition-colors">
              <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Create Word List</h3>
            <p className="text-sm text-slate-600">Add spelling words</p>
          </button>

          <button
            onClick={() => navigate('/classes')}
            className="p-4 border-2 border-dashed border-slate-200 rounded-2xl hover:border-amber-500 hover:bg-amber-50 transition-colors text-left group"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">View Reports</h3>
            <p className="text-sm text-slate-600">Check class progress</p>
          </button>
        </div>
      </Card>

      {/* Getting Started Guide (if no classes) */}
      {stats?.totalClasses === 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">🚀 Getting Started</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Create Your First Class</h3>
                  <p className="text-slate-600 text-sm mt-1">
                    Set up a class with a name, year level, and age rating
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Add Students</h3>
                  <p className="text-slate-600 text-sm mt-1">
                    Add students to your class and generate their login credentials
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Create Word Lists</h3>
                  <p className="text-slate-600 text-sm mt-1">
                    Add vocabulary words that will appear in student stories
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate('/classes/new')}
                className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
              >
                Create Your First Class
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
